import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '@/lib/theme';
import type { BlockedSlot } from '@/types';

interface AddBlockedSlotFormProps {
  onAdd: (slot: Omit<BlockedSlot, 'id' | 'barber_id' | 'created_at'>) => Promise<void>;
  onClose: () => void;
}

function AddBlockedSlotForm({ onAdd, onClose }: AddBlockedSlotFormProps) {
  const [mode, setMode] = useState<'full_day' | 'time_range'>('full_day');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    if (!date) { setErr('Por favor selecciona una fecha.'); return; }
    if (mode === 'time_range' && (!startTime || !endTime)) {
      setErr('Por favor establece hora de inicio y fin.'); return;
    }
    if (mode === 'time_range' && startTime >= endTime) {
      setErr('La hora de fin debe ser después de la hora de inicio.'); return;
    }
    setErr('');
    setSaving(true);
    try {
      await onAdd({
        blocked_date: date,
        start_time: mode === 'time_range' ? startTime : null,
        end_time: mode === 'time_range' ? endTime : null,
        reason: reason || null,
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="rounded-xl border p-5 mb-4"
      style={{ borderColor: theme.colors.primaryBorder, backgroundColor: theme.colors.bgSecondary }}
    >
      <p className="text-white text-sm font-semibold mb-4">Bloquear Tiempo</p>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-4">
        {(['full_day', 'time_range'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
            style={{
              backgroundColor: mode === m ? theme.colors.primary : theme.colors.bgSurface,
              color: mode === m ? '#000' : theme.colors.textSecondary,
              border: `1px solid ${mode === m ? theme.colors.primary : theme.colors.border}`,
            }}
          >
            {m === 'full_day' ? 'Todo el día' : 'Rango de horas'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className={mode === 'full_day' ? 'col-span-2' : ''}>
          <label className="text-xs mb-1 block" style={{ color: theme.colors.textMuted }}>Fecha</label>
          <input
            type="date"
            value={date}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
            style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border, colorScheme: 'dark' }}
          />
        </div>

        {mode === 'time_range' && (
          <>
            <div>
              <label className="text-xs mb-1 block" style={{ color: theme.colors.textMuted }}>Hora inicio</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
                style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border, colorScheme: 'dark' }}
              />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: theme.colors.textMuted }}>Hora fin</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
                style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border, colorScheme: 'dark' }}
              />
            </div>
          </>
        )}

        <div className="col-span-2">
          <label className="text-xs mb-1 block" style={{ color: theme.colors.textMuted }}>Motivo (opcional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ej. Cita personal"
            className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none placeholder:text-white/20"
            style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
          />
        </div>
      </div>

      {err && <p className="text-red-400 text-xs mt-2">{err}</p>}

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-white/5 transition-colors"
          style={{ color: theme.colors.textMuted }}
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors"
          style={{ backgroundColor: theme.colors.primary, color: '#000' }}
        >
          {saving ? 'Guardando…' : 'Bloquear'}
        </button>
      </div>
    </motion.div>
  );
}

interface BlockedSlotsListProps {
  blockedSlots: BlockedSlot[];
  onAdd: (slot: Omit<BlockedSlot, 'id' | 'barber_id' | 'created_at'>) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}

export default function BlockedSlotsList({ blockedSlots, onAdd, onRemove }: BlockedSlotsListProps) {
  const [showForm, setShowForm] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const handleRemove = async (id: string) => {
    setRemoving(id);
    try { await onRemove(id); } finally { setRemoving(null); }
  };

  const formatDate = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-white text-sm font-semibold">Tiempos Bloqueados</p>
          <p className="text-xs mt-0.5" style={{ color: theme.colors.textMuted }}>
            Fechas o rangos de tiempo no disponibles para citas
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
          style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary, border: `1px solid ${theme.colors.primaryBorder}` }}
        >
          <i className="ri-add-line text-sm" />
          Bloquear Tiempo
        </button>
      </div>

      <AnimatePresence>
        {showForm && (
          <AddBlockedSlotForm onAdd={onAdd} onClose={() => setShowForm(false)} />
        )}
      </AnimatePresence>

      {blockedSlots.length === 0 ? (
        <div
          className="rounded-xl border border-dashed flex flex-col items-center justify-center py-10"
          style={{ borderColor: theme.colors.border }}
        >
          <div className="w-10 h-10 flex items-center justify-center mb-2">
            <i className="ri-calendar-check-line text-2xl" style={{ color: theme.colors.textDisabled }} />
          </div>
          <p className="text-sm" style={{ color: theme.colors.textDisabled }}>Sin tiempos bloqueados</p>
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence>
            {blockedSlots.map((slot) => (
              <motion.div
                key={slot.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                className="flex items-center gap-4 rounded-xl border px-4 py-3"
                style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.bgSurface }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ backgroundColor: theme.colors.dangerMuted }}
                >
                  <i className="ri-forbid-line text-sm" style={{ color: theme.colors.danger }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{formatDate(slot.blocked_date)}</p>
                  <p className="text-xs mt-0.5" style={{ color: theme.colors.textMuted }}>
                    {slot.start_time && slot.end_time
                      ? `${slot.start_time.slice(0, 5)} – ${slot.end_time.slice(0, 5)}`
                      : 'Todo el día'}
                    {slot.reason ? ` · ${slot.reason}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleRemove(slot.id)}
                  disabled={removing === slot.id}
                  className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors hover:bg-red-500/10 disabled:opacity-40"
                  style={{ color: theme.colors.textDisabled }}
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
